import { Component, OnInit, inject, signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { ActivatedRoute } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import { PublicPaymentService } from '../../api/gen';
import { LicensePlateComponent } from '../home/license-plate/license-plate.component';

/**
 * A site's own payment page — what the QR printed on the site's signs points at.
 *
 * The home page's guest lookup, restricted to one site: a plate typed here is only matched against
 * stays at this parking space. Shown the same way whether or not the visitor is signed in, because the
 * person scanning a sign in a car park wants to pay, not to see their dashboard.
 */
@Component({
  selector: 'app-site',
  standalone: true,
  imports: [TranslatePipe, MatProgressSpinnerModule, LicensePlateComponent],
  templateUrl: './site.component.html',
  // Same look as the guest home page, so the global and the site page read as one app.
  styleUrl: '../home/home.component.scss'
})
export class SiteComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly paymentService = inject(PublicPaymentService);

  readonly parkingSpaceId = signal('');
  readonly spaceName = signal('');
  readonly isLoading = signal(true);
  readonly notFound = signal(false);

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('parkingSpaceId') ?? '';
    this.parkingSpaceId.set(id);

    // One request per page load; the lookup itself is one request per search.
    this.paymentService.publicPaymentSiteParkingSpaceIdConfigGet(id).subscribe({
      next: (config) => {
        this.spaceName.set(config?.spaceName ?? '');
        this.isLoading.set(false);
      },
      error: (err: HttpErrorResponse) => {
        this.isLoading.set(false);
        if (err?.status === 404) {
          this.notFound.set(true);
          return;
        }
        // The name is decoration; the lookup still works and reports a missing site on its own.
        console.error('Failed to load site', err);
      }
    });
  }
}
